import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, getFanToken, setFanToken, formatApiError } from "../lib/api";

/**
 * Fan session — spectator accounts that can purchase PPV and watch livestreams.
 * Fans NEVER have staff privileges (server enforces role separation).
 * Fan tokens are stored under `bufl_fan_token` and live for 30 days.
 */
const FanAuthContext = createContext(null);

export function FanAuthProvider({ children }) {
  const [fan, setFan] = useState(undefined); // undefined = loading, null = anon
  const [error, setError] = useState(null);

  const fetchMe = useCallback(async () => {
    if (!getFanToken()) {
      setFan(null);
      return null;
    }
    try {
      const { data } = await api.get("/fan/me");
      setFan(data);
      return data;
    } catch (_e) {
      setFanToken(null);
      setFan(null);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    setError(null);
    try {
      const { data } = await api.post("/fan/login", { email, password });
      setFanToken(data.token);
      await fetchMe();
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const register = async (email, password, full_name) => {
    setError(null);
    try {
      const { data } = await api.post("/fan/register", { email, password, full_name });
      setFanToken(data.token);
      await fetchMe();
      return data.user;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = () => {
    setFanToken(null);
    setFan(null);
  };

  return (
    <FanAuthContext.Provider
      value={{
        fan,
        user: fan?.user,
        entitlements: fan?.entitlements || [],
        purchases: fan?.purchases || [],
        error,
        login,
        register,
        logout,
        refresh: fetchMe,
      }}
    >
      {children}
    </FanAuthContext.Provider>
  );
}

export const useFanAuth = () => {
  const ctx = useContext(FanAuthContext);
  if (!ctx) throw new Error("useFanAuth must be used within FanAuthProvider");
  return ctx;
};
